import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { AlertCircle, Bot, Mic, MicOff, Send, Sparkles, UserRound, Volume2, X } from 'lucide-react';
import { apiAiAssistanceSendMessage, apiTextToSpeech } from '../api/client';

const starterPromptKeys = ['aiAssistanceStarterOne', 'aiAssistanceStarterTwo', 'aiAssistanceStarterThree'];

// ─── Voice Chat Modal ────────────────────────────────────────────────────────

const VOICE_STATE = { IDLE: 'idle', LISTENING: 'listening', THINKING: 'thinking', SPEAKING: 'speaking' };

function detectLang(text) {
  if (!text) return navigator.language || 'en-US';
  if (/[؀-ۿ]/.test(text)) return 'ar-SA';
  if (/[一-鿿㐀-䶿]/.test(text)) return 'zh-CN';
  if (/[؀-ۿݐ-ݿﭐ-﷿]/.test(text)) return 'ur-PK';
  if (/\b(le|la|les|un|une|des|est|sont|avec|pour|dans|que|qui)\b/i.test(text)) return 'fr-FR';
  return navigator.language || 'en-US';
}

function VoiceChatModal({ t, onClose }) {
  const [voiceState, setVoiceState] = useState(VOICE_STATE.IDLE);
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const audioBlobUrlRef = useRef(null);
  const abortRef = useRef(false);
  const transcriptRef = useRef('');
  const detectedLangRef = useRef(navigator.language || 'en-US');

  const isSupported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (audioBlobUrlRef.current) {
      URL.revokeObjectURL(audioBlobUrlRef.current);
      audioBlobUrlRef.current = null;
    }
  }, []);

  const speak = useCallback(async (text) => {
    stopSpeaking();
    setVoiceState(VOICE_STATE.SPEAKING);
    try {
      const blobUrl = await apiTextToSpeech(text);
      if (abortRef.current) { URL.revokeObjectURL(blobUrl); return; }
      audioBlobUrlRef.current = blobUrl;
      const audio = new Audio(blobUrl);
      audioRef.current = audio;
      audio.playbackRate = 1.15;
      await new Promise((resolve, reject) => {
        audio.onended = () => {
          URL.revokeObjectURL(blobUrl);
          audioBlobUrlRef.current = null;
          audioRef.current = null;
          resolve();
        };
        audio.onerror = (e) => reject(new Error(`Audio playback error: ${e.message ?? e}`));
        audio.play().catch(reject);
      });
      if (!abortRef.current) setVoiceState(VOICE_STATE.IDLE);
    } catch (err) {
      if (!abortRef.current) {
        setError(err instanceof Error ? err.message : 'TTS failed');
        setVoiceState(VOICE_STATE.IDLE);
      }
    }
  }, [stopSpeaking]);

  const sendToAi = useCallback(async (text) => {
    if (!text.trim()) return;
    setVoiceState(VOICE_STATE.THINKING);
    setError('');
    const context = history
      .slice(-6)
      .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.text}`)
      .join('\n');
    try {
      const res = await apiAiAssistanceSendMessage({ message: text, context });
      const answer = res.answer ?? res.Answer ?? '';
      const replyText = answer || t('aiAssistanceEmptyAnswer');
      setAiResponse(replyText);
      setHistory((prev) => [
        ...prev,
        { role: 'user', text },
        { role: 'assistant', text: replyText },
      ]);
      await speak(replyText);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('aiAssistanceFailed');
      setError(msg);
      setVoiceState(VOICE_STATE.IDLE);
    }
  }, [history, speak, t]);

  const startListening = useCallback(() => {
    if (!isSupported) return;
    stopSpeaking();
    transcriptRef.current = '';
    setTranscript('');
    setAiResponse('');
    setError('');

    const SR = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = detectedLangRef.current || document.documentElement.lang || 'en-US';
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    recognitionRef.current = rec;

    rec.onstart = () => setVoiceState(VOICE_STATE.LISTENING);

    rec.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += chunk;
        else interim += chunk;
      }
      const combined = final || interim;
      transcriptRef.current = combined;
      detectedLangRef.current = detectLang(combined);
      setTranscript(combined);
    };

    rec.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'denied') {
        setError(t('micDenied'));
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setError(event.error);
      }
      setVoiceState(VOICE_STATE.IDLE);
    };

    rec.onend = () => {
      const captured = transcriptRef.current.trim();
      if (captured) {
        sendToAi(captured);
      } else {
        setVoiceState(VOICE_STATE.IDLE);
      }
    };

    rec.start();
  }, [isSupported, sendToAi, stopSpeaking, t]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
  }, []);

  const handleMicClick = () => {
    if (voiceState === VOICE_STATE.LISTENING) {
      stopListening();
    } else if (voiceState === VOICE_STATE.SPEAKING) {
      stopSpeaking();
      setVoiceState(VOICE_STATE.IDLE);
    } else if (voiceState === VOICE_STATE.IDLE) {
      startListening();
    }
  };

  // cleanup on unmount
  useEffect(() => {
    abortRef.current = false;
    return () => {
      abortRef.current = true;
      recognitionRef.current?.abort();
      stopSpeaking();
    };
  }, [stopSpeaking]);

  const micButtonColor =
    voiceState === VOICE_STATE.LISTENING
      ? 'bg-pink-500 hover:bg-pink-600 shadow-pink-300'
      : voiceState === VOICE_STATE.SPEAKING
      ? 'bg-green-500 hover:bg-green-600 shadow-green-300'
      : 'bg-brand-600 hover:bg-brand-700 shadow-brand-300';

  const stateLabel =
    voiceState === VOICE_STATE.LISTENING
      ? t('listening')
      : voiceState === VOICE_STATE.THINKING
      ? t('thinking')
      : voiceState === VOICE_STATE.SPEAKING
      ? t('aiSpeaking')
      : t('tapToSpeak');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="relative flex w-full max-w-md flex-col items-center gap-6 rounded-3xl border border-slate-200 bg-white p-8 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
            <Bot size={28} />
          </span>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('voiceChatTitle')}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('voiceChatHint')}</p>
        </div>

        {!isSupported ? (
          <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            {t('voiceChatUnsupported')}
          </p>
        ) : (
          <>
            {/* Transcript / response display */}
            <div className="flex w-full flex-col gap-3 text-sm">
              {transcript && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {t('youSaid')}
                  </span>
                  <p className="text-slate-700 dark:text-slate-200">{transcript}</p>
                </div>
              )}
              {aiResponse && (
                <div className="rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3 dark:border-brand-800 dark:bg-brand-950/30">
                  <div className="mb-1 flex items-center gap-1.5">
                    <Bot size={13} className="text-brand-600 dark:text-brand-400" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">
                      AI
                    </span>
                    {voiceState === VOICE_STATE.SPEAKING && (
                      <Volume2 size={13} className="animate-pulse text-brand-500" />
                    )}
                  </div>
                  <p className="text-slate-700 dark:text-slate-200">{aiResponse}</p>
                </div>
              )}
              {error && (
                <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            {/* Mic button with pulse ring */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative flex items-center justify-center">
                {voiceState === VOICE_STATE.LISTENING && (
                  <span className="absolute inline-flex h-24 w-24 rounded-full bg-red-400 opacity-40 animate-ping" />
                )}
                <button
                  type="button"
                  onClick={handleMicClick}
                  disabled={voiceState === VOICE_STATE.THINKING}
                  className={`relative z-10 inline-flex h-20 w-20 items-center justify-center rounded-full text-white shadow-lg transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 ${micButtonColor}`}
                  aria-label={stateLabel}
                >
                  {voiceState === VOICE_STATE.THINKING ? (
                    <span className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : voiceState === VOICE_STATE.LISTENING ? (
                    <MicOff size={30} />
                  ) : voiceState === VOICE_STATE.SPEAKING ? (
                    <Volume2 size={30} />
                  ) : (
                    <Mic size={30} />
                  )}
                </button>
              </div>
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{stateLabel}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

function AiAssistancePage({ t, onToast }) {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      textKey: 'aiAssistanceWelcomeMessage',
    },
  ]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [voiceOpen, setVoiceOpen] = useState(false);

  const recentContext = useMemo(
    () =>
      messages
        .slice(-6)
        .map((message) => {
          const text = message.textKey ? t(message.textKey) : message.text;
          return `${message.role === 'user' ? t('contextUser') : t('contextAssistant')}: ${text}`;
        })
        .join('\n'),
    [messages, t],
  );

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: trimmed,
    };

    setMessages((prev) => [...prev, userMessage]);
    setDraft('');
    setError('');
    setLoading(true);

    try {
      const response = await apiAiAssistanceSendMessage({
        message: trimmed,
        context: recentContext,
      });
      const answer = response.answer ?? response.Answer ?? '';
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          text: answer || t('aiAssistanceEmptyAnswer'),
        },
      ]);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('aiAssistanceFailed');
      setError(message);
      onToast?.(message, 'error');
      setMessages((prev) => prev.filter((item) => item.id !== userMessage.id));
      setDraft(trimmed);
    } finally {
      setLoading(false);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    void sendMessage(draft);
  };

  return (
    <>
      {voiceOpen && <VoiceChatModal t={t} onClose={() => setVoiceOpen(false)} />}

      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#1A1A1A] via-brand-600 to-green-500 p-6 text-white shadow-xl shadow-brand/20 dark:border-slate-800">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-sm font-semibold">
                <Sparkles size={16} />
                {t('navAIAssistance')}
              </div>
              <h1 className="text-3xl font-bold md:text-4xl">{t('aiAssistanceHeroTitle')}</h1>
              <p className="mt-3 text-sm leading-6 text-brand-50 md:text-base">
                {t('aiAssistanceHeroBody')}
              </p>
            </div>
            <div className="flex flex-col items-start gap-3">
              <button
                type="button"
                onClick={() => setVoiceOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-white/20 px-5 py-3 text-sm font-semibold text-white shadow-lg backdrop-blur-sm transition hover:bg-white/30 active:scale-95"
              >
                <Mic size={18} />
                {t('voiceChat')}
              </button>
            </div>
          </div>
        </section>

        <div className="grid min-h-[620px] gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <section className="flex min-h-[620px] flex-col rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="border-b border-slate-200 p-5 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                  <Bot size={22} />
                </span>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('coretaskAssistant')}</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('aiAssistanceCardHelp')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              {messages.map((message) => {
                const isUser = message.role === 'user';
                return (
                  <article
                    key={message.id}
                    className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    {!isUser && (
                      <span className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                        <Bot size={18} />
                      </span>
                    )}
                    <div
                      className={`max-w-[82%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-6 ${
                        isUser
                          ? 'bg-brand-600 text-white'
                          : 'border border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200'
                      }`}
                    >
                      {message.textKey ? t(message.textKey) : message.text}
                    </div>
                    {isUser && (
                      <span className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white dark:bg-white dark:text-slate-900">
                        <UserRound size={18} />
                      </span>
                    )}
                  </article>
                );
              })}
              {loading && (
                <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                    <Bot size={18} />
                  </span>
                  {t('thinking')}
                </div>
              )}
            </div>

            <form onSubmit={submit} className="border-t border-slate-200 p-5 dark:border-slate-800">
              {error && (
                <div className="mb-4 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <div className="flex gap-3">
                <textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void sendMessage(draft);
                    }
                  }}
                  rows={2}
                  placeholder={t('askAiAssistance')}
                  className="min-h-[52px] flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-brand-400 focus:bg-white focus:ring-4 focus:ring-brand-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-brand-500 dark:focus:ring-brand-900/40"
                />
                <button
                  type="submit"
                  disabled={loading || !draft.trim()}
                  className="inline-flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-2xl bg-brand-600 text-white shadow-lg shadow-brand/20 transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                  title={t('send')}
                >
                  <Send size={20} />
                </button>
              </div>
            </form>
          </section>

          <aside className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">{t('tryAsking')}</h2>
            <div className="mt-4 space-y-3">
              {starterPromptKeys.map((promptKey) => (
                <button
                  key={promptKey}
                  type="button"
                  onClick={() => setDraft(t(promptKey))}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-left text-sm leading-5 text-slate-600 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-brand-800 dark:hover:bg-brand-950/40"
                >
                  {t(promptKey)}
                </button>
              ))}
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setVoiceOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border border-brand-200 bg-brand-50 p-3 text-sm font-medium text-brand-700 transition hover:bg-brand-100 dark:border-brand-800 dark:bg-brand-950/30 dark:text-brand-300 dark:hover:bg-brand-900/40"
              >
                <Mic size={16} />
                {t('voiceChat')}
              </button>
            </div>
            <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-xs leading-5 text-slate-500 dark:bg-slate-950 dark:text-slate-400">
              {t('aiAssistanceReadonlyHint')}
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

export default AiAssistancePage;
